import type { GridId, GridPosition, ScreenLayout } from "@observe-tech/utils";

/**
 * Half screen strategy function type
 * @param layout The screen layout to modify
 * @param gridId The grid ID of the position being focused
 * @param position The grid position being focused
 * @returns The modified layout
 */
export type HalfScreenStrategy = (
  layout: ScreenLayout,
  gridId: GridId,
  position: GridPosition,
) => ScreenLayout;

/**
 * Default half screen strategy that assigns focus based on column position
 * and handles occupied sides gracefully
 */
export const fillAvailableStrategy: HalfScreenStrategy = (
  layout: ScreenLayout,
  gridId: GridId,
  position: GridPosition,
): ScreenLayout => {
  const { columns } = layout.screenData.gridLayout;
  const column = position.column;

  if (layout.screenData.halfScreenLeft === gridId) {
    // If already set to left, remove the focus
    layout.screenData.halfScreenLeft = null;
  } else if (layout.screenData.halfScreenRight === gridId) {
    // If already set to right, remove the focus
    layout.screenData.halfScreenRight = null;
  } else {
    // Position isn't focused, determine which side to use
    const isLeftSide = column <= Math.floor(columns / 2);
    const preferredSide = isLeftSide ? "left" : "right";

    if (preferredSide === "left") {
      if (layout.screenData.halfScreenLeft === null) {
        // Preferred side (left) is available
        layout.screenData.halfScreenLeft = gridId;
      } else if (layout.screenData.halfScreenRight === null) {
        // Preferred side occupied, use opposite side (right)
        layout.screenData.halfScreenRight = gridId;
      } else {
        // Both sides occupied, use preferred side anyway
        layout.screenData.halfScreenLeft = gridId;
      }
    } else {
      if (layout.screenData.halfScreenRight === null) {
        // Preferred side (right) is available
        layout.screenData.halfScreenRight = gridId;
      } else if (layout.screenData.halfScreenLeft === null) {
        // Preferred side occupied, use opposite side (left)
        layout.screenData.halfScreenLeft = gridId;
      } else {
        // Both sides occupied, use preferred side anyway
        layout.screenData.halfScreenRight = gridId;
      }
    }
  }

  return layout;
};

/**
 * Simple strategy that strictly assigns based on column position
 * Uses ceil to determine left/right split and always assigns to the determined side
 * If column is in the middle, it goes to the right side
 */
export const middleGoesRightStrategy: HalfScreenStrategy = (
  layout: ScreenLayout,
  gridId: GridId,
  position: GridPosition,
): ScreenLayout => {
  if (layout.screenData.halfScreenLeft === gridId) {
    layout.screenData.halfScreenLeft = null;
  } else if (layout.screenData.halfScreenRight === gridId) {
    layout.screenData.halfScreenRight = null;
  } else {
    const { columns } = layout.screenData.gridLayout;
    const column = position.column;

    if (column <= Math.ceil(columns / 2)) {
      layout.screenData.halfScreenLeft = gridId;
    } else {
      layout.screenData.halfScreenRight = gridId;
    }
  }

  return layout;
};

/**
 * Available half screen strategies
 */
export const halfScreenStrategies = {
  fillAvailableStrategy,
  middleGoesRightStrategy,
} as const;

export type HalfScreenStrategyName = keyof typeof halfScreenStrategies;
